import {
  App,
  Button,
  Card,
  Form,
  InputNumber,
  Modal,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import { FC, useEffect, useState } from "react";
import { useContract } from "../../../contexts/ContractContext";
import { api } from "../../../services/api";
import { IContractItem, IItem, ModalProps } from "../../../types";
import { formatters } from "../../../utils/formatters";
import { parsers } from "../../../utils/parsers";
import { getItems } from "../../../services/repositories";

const { Text, Paragraph } = Typography;

const ContractItemModal: FC<ModalProps<IItem>> = ({
  onClose,
  onRefreshData,
  isModalOpen,
  data: contractItem,
}) => {
  const { message } = App.useApp();

  const { contract } = useContract();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<IItem[]>([]);
  const [item, setItem] = useState<IItem>();

  const IS_EDIT_ITEM = !!contractItem;

  useEffect(() => {
    const getData = async () => {
      if (contractItem) {
        setItems([contractItem]);
        setItem(contractItem);
        form.setFieldsValue({
          itemId: contractItem.id,
          number: contractItem.meta?.pivot_number,
          value: contractItem.meta?.pivot_value,
          cost: contractItem.meta?.pivot_cost,
          quantity: contractItem.meta?.pivot_quantity,
        });
        return;
      }

      let { data } = await getItems(
        { page: 1, perPage: 1000 },
        { notInContractId: contract?.id }
      );

      setItems(data);
    };
    if (isModalOpen) {
      getData();
    }
  }, [contract?.id, contractItem, form, isModalOpen]);

  const handleCloseModal = () => {
    if (loading) {
      return;
    }
    setItem(undefined);
    form.resetFields();
    onClose();
  };

  const onSubmit = async () => {
    const values = await form
      .validateFields()
      .then((values) => {
        return values;
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      })
      .finally(() => setLoading(false));

    if (!values) {
      return;
    }
    setLoading(true);
    handleSubmitForm(values)
      .then((data) => {
        message.success(
          `Item ${
            data.config.method === "put" ? "atualizado" : "criado"
          } com sucesso!`
        );
        handleCloseModal();
        if (onRefreshData) {
          onRefreshData();
        }
      })
      .catch((error) => {
        if (error.response.data.errors) {
          error.response.data.errors.forEach((err: any) =>
            message.error(err.message)
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleSubmitForm = async (values: IContractItem) => {
    if (contractItem) {
      return await api.put<IContractItem>(
        `/contracts/${contract?.id}/items/${contractItem.meta?.pivot_id}`,
        values
      );
    }
    return await api.post<IContractItem>(
      `/contracts/${contract?.id}/items`,
      values
    );
  };

  const handleSelectItem = (itemId: string) => {
    let _item = items.find((item) => item.id === itemId);
    setItem(_item ?? undefined);
    form.setFieldsValue({
      value: _item?.value,
      cost: _item?.cost,
    });
  };

  return (
    <Modal
      title={IS_EDIT_ITEM ? "Editar item" : "Adicionar item de serviço"}
      open={isModalOpen}
      style={{ top: 260 }}
      onCancel={handleCloseModal}
      footer={[
        <Button key="back" onClick={handleCloseModal} danger disabled={loading}>
          Cancelar
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={onSubmit}
          loading={loading}
        >
          Salvar
        </Button>,
      ]}
    >
      {!!item && <ItemCard item={item} />}
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" name="contract_item_form_modal">
          <Form.Item
            name="itemId"
            label="Item"
            rules={[
              {
                required: true,
                message: "Por favor, selecione um item.",
              },
            ]}
          >
            <Select
              disabled={IS_EDIT_ITEM}
              optionLabelProp="label"
              optionFilterProp="label"
              showSearch
              allowClear
              options={items?.map((item) => {
                return {
                  value: item.id,
                  label: item.name,
                };
              })}
              onChange={handleSelectItem}
            />
          </Form.Item>
          <Form.Item
            name="number"
            label="Número"
            rules={[
              {
                required: true,
                message: "Por favor, insira o número do item.",
              },
            ]}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="value"
            label="Preço"
            rules={[
              {
                required: true,
                message: "Por favor, insira o valor do item.",
              },
            ]}
          >
            <InputNumber
              formatter={formatters.currency}
              parser={parsers.currency}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name="cost"
            label="Custo"
            rules={[
              {
                required: true,
                message: "Por favor, insira o custo do item.",
              },
            ]}
          >
            <InputNumber
              formatter={formatters.currency}
              parser={parsers.currency}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name="quantity"
            label="Quantidade"
            rules={[
              {
                required: true,
                message: "Por favor, insira a quantidade do item.",
              },
            ]}
          >
            <InputNumber
              disabled={IS_EDIT_ITEM}
              min={1}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export const ItemCard = ({ item }: { item?: IItem }) => {
  return (
    <Card
      style={{
        zIndex: -1,
        display: !!item ? "initial" : "none",
        top: 10,
        width: 520,
        position: "fixed",
        left: "50%",
        transform: "translate(-50%, 0)",
      }}
    >
      <Space direction="vertical" size={0} style={{ width: "100%" }}>
        <Text strong>Descrição:</Text>
        <Paragraph ellipsis={{ rows: 2 }}>{item?.description}</Paragraph>
      </Space>
      <Space direction="vertical" size={0} style={{ width: "100%" }}>
        <Text strong>Tipos de Equipamento:</Text>
        <Paragraph ellipsis={{ rows: 1 }}>
          {item?.equipmentTypes
            ?.map((type) => type.name)
            .toString()
            .replaceAll(",", ", ")}
        </Paragraph>
      </Space>
      <Space direction="vertical" size={0} style={{ width: "100%" }}>
        <Text strong>Tipos de Serviços:</Text>
        <Paragraph ellipsis={{ rows: 1 }}>
          {item?.serviceTypes
            ?.map((type) => type.name)
            .toString()
            .replaceAll(",", ", ")}
        </Paragraph>
      </Space>
    </Card>
  );
};

export { ContractItemModal };
